import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { oneClickDemo } from "../components/Layout";
import { ArrowRight, Compass, Clock, Building2, MapPinned, Layers, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import api, { apiError } from "../lib/api";
import { track } from "../lib/analytics";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import ApprovalCard from "../components/ApprovalCard";
import { PROJECT_TYPES } from "../lib/constants";

function SampleRoadmap() {
  const [sample, setSample] = useState(null);
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/sample-roadmap").then(({ data }) => setSample(data)).catch(() => setSample(false));
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (sample === false) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14" id="sample" data-testid="sample-roadmap-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#059669]">See a live example</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#0B1F3A] mt-2">This is a real output — explore it.</h2>
          <p className="text-sm text-[#475569] mt-1 max-w-xl">A 200 MW solar project in Rajasthan with a dedicated 220 kV line. Expand any department below — then generate yours in 60 seconds.</p>
        </div>
        {stats?.show_counter && (
          <span className="bg-white border border-[#059669]/40 text-[#059669] rounded-full px-4 py-1.5 text-xs font-bold" data-testid="usage-counter">
            {stats.roadmaps_generated} roadmaps generated so far
          </span>
        )}
      </div>

      {!sample ? (
        <div className="h-64 bg-[#0B1F3A]/10 rounded-xl animate-pulse mt-6" />
      ) : (
        <div className="mt-6 bg-white border border-[#0B1F3A]/10 rounded-2xl p-4 sm:p-6">
          <div className="flex flex-wrap gap-x-5 gap-y-2 pb-4 border-b border-[#0B1F3A]/10">
            <p className="font-serif font-semibold text-[#0B1F3A]">{sample.name}</p>
            <span className="text-xs text-[#475569] inline-flex items-center gap-1.5"><Layers size={13} className="text-[#1D4ED8]" /> {sample.roadmap.total_approvals} approvals</span>
            <span className="text-xs text-[#475569] inline-flex items-center gap-1.5"><Building2 size={13} className="text-[#1D4ED8]" /> {sample.roadmap.departments_count} departments</span>
            <span className="text-xs text-[#475569] inline-flex items-center gap-1.5"><CalendarClock size={13} className="text-[#1D4ED8]" /> ~{sample.roadmap.critical_path_days} days critical path (indicative)</span>
          </div>
          <Accordion type="multiple" className="mt-2" data-testid="sample-roadmap-accordion">
            {sample.roadmap.departments.map((d) => (
              <AccordionItem key={d.name} value={d.name} className="border-b border-[#0B1F3A]/10">
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex items-center gap-3 text-left">
                    <span className="font-medium text-[#0B1F3A] text-sm">{d.name}</span>
                    <span className="text-[10px] bg-[#1D4ED8] text-white rounded-full px-2 py-0.5">{d.approvals.length}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pb-4">
                  {d.approvals.map((a) => <ApprovalCard key={a.key} approval={a} />)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <button onClick={() => navigate("/wizard")} className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#1D4ED8] text-white rounded-md px-6 py-3 font-semibold text-sm hover:bg-[#1638A8]" data-testid="sample-wizard-cta">
            Generate mine in 60 seconds <ArrowRight size={16} />
          </button>
        </div>
      )}
    </section>
  );
}

const APPROVAL_CATEGORIES = ["Section 68 & 164", "Connectivity & GNA", "PTCC", "CEIG & charging", "RoW & land conversion", "Forest & wildlife", "NH / SH / railway / river crossings", "Aviation & Defence NOCs", "Pollution consent", "Net metering & rooftop"];

function CoverageStrip() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-14" data-testid="coverage-strip">
      <div className="bg-white border border-[#0B1F3A]/10 rounded-2xl p-6 sm:p-8">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#059669]">Coverage</p>
        <div className="grid sm:grid-cols-2 gap-6 mt-3">
          <div>
            <p className="text-sm font-semibold text-[#0B1F3A]">Project types</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {PROJECT_TYPES.map((p) => (
                <span key={p.value} className="text-[11px] bg-blue-50 text-[#1D4ED8] border border-[#1D4ED8]/20 rounded-full px-3 py-1">{p.label.split(" (")[0]}</span>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-[#0B1F3A]">Approval categories</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {APPROVAL_CATEGORIES.map((c) => (
                <span key={c} className="text-[11px] bg-emerald-50 text-[#059669] border border-[#059669]/20 rounded-full px-3 py-1">{c}</span>
              ))}
            </div>
          </div>
        </div>
        <p className="text-xs text-[#475569] mt-4">State-specific depth for Rajasthan, Maharashtra, Gujarat, Karnataka and Uttar Pradesh — national baseline everywhere else, with honest coverage banners.</p>
      </div>
    </section>
  );
}

function ConsultCTA() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", note: "" });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { type: "consult", ...form });
      setSent(true);
      track("expert_consult_clicked", { source: "home" });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-4" id="consult" data-testid="consult-section">
      <div className="band-navy text-white rounded-2xl p-6 sm:p-10 grid lg:grid-cols-2 gap-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#34D399]">Need execution, not just a map?</p>
          <h2 className="font-serif text-2xl sm:text-3xl mt-2">Book a consultation with a Solarian expert.</h2>
          <p className="text-sm text-white/70 mt-3 max-w-md">We file these approvals every week — Section 68/164, PTCC, CEIG, crossings, open access, captive structuring. Pre-bid to charging.</p>
        </div>
        {sent ? (
          <div className="flex items-center" data-testid="consult-home-confirmation">
            <p className="text-sm bg-white/10 border border-[#34D399]/40 rounded-xl px-5 py-4">Thanks — a Solarian expert will reach out within 1 business day.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="grid grid-cols-2 gap-3">
            <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="col-span-1 bg-white/95 text-[#0B1F3A] rounded-md px-3 py-2.5 text-sm" data-testid="consult-home-name" />
            <input required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="col-span-1 bg-white/95 text-[#0B1F3A] rounded-md px-3 py-2.5 text-sm" data-testid="consult-home-email" />
            <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="col-span-1 bg-white/95 text-[#0B1F3A] rounded-md px-3 py-2.5 text-sm" data-testid="consult-home-phone" />
            <input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="col-span-1 bg-white/95 text-[#0B1F3A] rounded-md px-3 py-2.5 text-sm" data-testid="consult-home-company" />
            <textarea placeholder="A line about your project (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} className="col-span-2 bg-white/95 text-[#0B1F3A] rounded-md px-3 py-2.5 text-sm" data-testid="consult-home-note" />
            <button type="submit" className="col-span-2 bg-[#059669] text-white rounded-md py-3 text-sm font-bold hover:bg-[#047857]" data-testid="consult-home-submit">Book a consultation</button>
          </form>
        )}
      </div>
    </section>
  );
}

const PLANT = ["Feasibility & bid", "Planning, survey & financing", "Engineering & procurement", "Land & site mobilization", "Foundations & civil works", "Erection & structures", "Cabling, stringing & electrical", "Integration & testing", "Charging & commissioning"];
const REG = ["R1 Pre-development approvals", "R2 Land & environment", "R3 Route & construction authorizations", "R4 Electrical safety (build stage)", "R5 Pre-commissioning", "R6 Charging & post-COD"];

export default function Home() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [demoLoading, setDemoLoading] = useState(false);

  const demo = async () => {
    setDemoLoading(true);
    try {
      await oneClickDemo(setUser, navigate);
    } catch {
      toast.error("Demo is warming up — try again in a moment");
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div>
      <section className="hero-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16 sm:pb-20">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#34D399]">Every approval. Every department. One roadmap.</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-4 max-w-3xl">
            Know every approval <span className="text-[#5B8CFF]">before</span> you break ground.
          </h1>
          <p className="text-base sm:text-lg text-white/75 mt-5 max-w-2xl leading-relaxed">
            RegNav maps the complete statutory and regulatory approval roadmap for your power project — every department, every document, every timeline. Built on 15+ years of regulatory depth at Solarian.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <div>
              <button onClick={() => navigate("/wizard")} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#1D4ED8] text-white rounded-md px-6 py-3.5 font-semibold hover:bg-[#1638A8] transition-all hover:-translate-y-0.5 shadow-lg shadow-blue-900/40" data-testid="hero-wizard-cta">
                Map my project's approvals <ArrowRight size={18} />
              </button>
              <p className="text-xs text-white/55 mt-2 text-center sm:text-left">Free 60-second roadmap — see it instantly. Verify only to download the branded PDF.</p>
            </div>
            <button onClick={demo} disabled={demoLoading} className="h-fit inline-flex items-center justify-center gap-2 border border-white/30 text-white rounded-md px-6 py-3.5 font-medium hover:bg-white/10 disabled:opacity-60 transition-colors" data-testid="hero-demo-cta">
              {demoLoading ? "Opening demo…" : "Explore the live demo"}
            </button>
          </div>
          <a href="#sample" className="inline-block mt-4 text-sm text-[#5B8CFF] underline underline-offset-4 hover:text-white transition-colors" data-testid="hero-sample-link">See a live example ↓</a>
          <div className="grid grid-cols-3 gap-3 mt-12 max-w-lg">
            {[["30+", "approvals mapped"], ["13+", "departments covered"], ["11", "states covered in depth"]].map(([v, l]) => (
              <div key={l} className="border-l-2 border-[#34D399] pl-3">
                <p className="font-serif text-2xl font-semibold">{v}</p>
                <p className="text-[11px] text-white/60 mt-0.5">{l}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-white/70 mt-6 max-w-xl" data-testid="hero-impact-line">
            What takes a consultant ~2 days to compile by hand, RegNav maps in 60 seconds — across 13+ departments.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { icon: Clock, text: "Approvals discovered late = months of delay", tile: "bg-blue-50 text-[#1D4ED8]" },
            { icon: Building2, text: "15+ agencies, zero single source", tile: "bg-emerald-50 text-[#059669]" },
            { icon: MapPinned, text: "The majority of project delays are regulatory — not construction", tile: "bg-blue-50 text-[#1D4ED8]" },
          ].map((c) => (
            <div key={c.text} className="bg-white border border-[#0B1F3A]/10 rounded-xl p-6 card-lift">
              <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${c.tile}`}><c.icon size={20} /></span>
              <p className="font-serif text-[#0B1F3A] mt-4 text-lg">{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="band-navy py-16 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="font-serif text-2xl sm:text-3xl">Every project runs on two roadmaps.</h2>
          <p className="text-white/70 mt-2 max-w-2xl text-sm sm:text-base">The one nobody plans is the one that stops the plant. RegNav maps it in 60 seconds.</p>
          <div className="mt-8 overflow-x-auto regnav-scroll pb-2">
            <div className="min-w-[980px]">
              <p className="text-[10px] uppercase tracking-widest text-[#5B8CFF] mb-1.5 font-semibold">Roadmap 1 — plant execution, 9 stages (everyone plans this)</p>
              <div className="flex gap-1.5">
                {PLANT.map((s, i) => (
                  <div key={s} className="flex-1 bg-[#1D4ED8] text-white rounded-md px-1.5 py-2.5 text-center text-[11px] font-medium leading-tight" title={s}>
                    <span className="block text-[9px] text-white/50 font-bold">{i + 1}</span>{s}
                  </div>
                ))}
              </div>
              <div className="flex gap-1.5 my-1.5 px-8">
                {REG.map((s) => <div key={s} className="flex-1 flex justify-center"><div className="h-6 border-l-2 border-dashed border-[#34D399]" /></div>)}
              </div>
              <p className="text-[10px] uppercase tracking-widest text-[#34D399] mb-1.5 font-semibold">Roadmap 2 — regulatory, 6 phases (nobody plans this — we do)</p>
              <div className="flex gap-1.5">
                {REG.map((s) => (
                  <div key={s} className="flex-1 border-2 border-[#059669] bg-[#059669]/15 text-white rounded-md px-1.5 py-2.5 text-center text-[11px] leading-tight">{s}</div>
                ))}
              </div>
            </div>
          </div>
          <p className="text-white/55 text-xs mt-6 max-w-2xl">
            Golden rule #1: Section 68 approval before even 1% of line work. Golden rule #5: connectivity before financial closure — lenders will ask. Plants stand built but inoperational, burning crores in idle capital — because Roadmap 2 was an afterthought.
          </p>
        </div>
      </section>

      <SampleRoadmap />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-0" id="about" data-testid="about-section">
        <div className="bg-white border border-[#0B1F3A]/10 rounded-2xl p-6 sm:p-10 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#059669]">What's in the name</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#0B1F3A] mt-2">
              <span className="text-[#1D4ED8]">Reg</span><span className="text-[#059669]">Nav</span> — <span className="italic">Your Regulatory Navigator</span>.
            </h2>
            <p className="text-sm sm:text-base text-[#475569] mt-3 max-w-2xl leading-relaxed">
              A navigator for the regulatory roadmap of India's power projects — the approvals, the departments, the documents and the sequence. Built by <span className="font-semibold"><span className="text-[#1D4ED8]">Solar</span><span className="text-[#059669]">ian</span></span>, the end-to-end consulting layer for India's energy transition — regulatory depth meets technical depth, pre-bid to charging.
            </p>
          </div>
          <div className="flex lg:flex-col gap-3">
            <div className="bg-[#F5F8FF] border border-[#1D4ED8]/20 rounded-xl px-5 py-4 text-center">
              <p className="font-sans font-extrabold text-2xl"><span className="text-[#1D4ED8]">Reg</span><span className="text-[#059669]">Nav</span></p>
              <p className="text-[10px] uppercase tracking-widest text-[#475569] mt-1">Regulatory Navigator</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 pb-10">
        <h2 className="font-serif text-2xl sm:text-3xl text-[#0B1F3A]">How it works</h2>
        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          {[
            { n: "1", t: "Describe your project", d: "60 seconds, plain language, 'Not sure' is always an option." },
            { n: "2", t: "Get your complete roadmap", d: "Instantly — every approval grouped by department and sequence, with documents and timelines." },
            { n: "3", t: "Execute with confidence", d: "Download the branded PDF, share it with your team, or book a Solarian expert." },
          ].map((s) => (
            <div key={s.n} className="bg-white border border-[#0B1F3A]/10 rounded-xl p-6 card-lift">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#1D4ED8] text-white font-bold text-sm">{s.n}</span>
              <h3 className="font-serif font-semibold text-[#0B1F3A] mt-3 text-lg">{s.t}</h3>
              <p className="text-sm text-[#475569] mt-1.5">{s.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm sm:text-base font-serif italic text-[#0B1F3A] border-l-2 border-[#059669] pl-4 max-w-2xl" data-testid="how-it-works-impact-strip">
          From 2 days of manual research to a 60-second roadmap — every department, document and timeline, before you break ground.
        </p>
        <div className="mt-8 band-navy text-white rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <Compass size={22} className="text-[#34D399]" />
            <h3 className="font-serif text-lg sm:text-xl font-semibold mt-2">Built by the people who file these approvals every week.</h3>
            <p className="text-sm text-white/70 mt-1 max-w-xl">Solarian — transmission &amp; renewable approvals across India: Section 68/164, PTCC, CEIG, Aviation &amp; Defence NOCs, open access and captive structuring. Pre-bid to charging.</p>
            <p className="text-sm text-[#34D399] mt-2 max-w-xl" data-testid="scale-signal-line">Built for one developer's project — works for every EPC, IPP and industrial power project in India.</p>
          </div>
          <Link to="/wizard" className="shrink-0 bg-[#059669] text-white rounded-md px-6 py-3 font-bold text-sm hover:bg-[#047857] transition-colors" data-testid="authority-wizard-cta">Start my roadmap</Link>
        </div>
      </section>

      <CoverageStrip />
      <ConsultCTA />
    </div>
  );
}
